import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { Sku } from "../api/models"
import { logOut } from "./globalSlice"
import { RootState } from "./store"
import Api from "../api/api"
import { FetchableState, useFetchableState } from "./fetchableState"
import { useSelector } from "react-redux"

// Subscription plans are skus with a subscription offering — the sku
// entities live in skusSlice; this slice only tracks which sku ids are
// currently offered (sorted by cheapest subscription offering) plus the
// user's extra trial days.

export type ActivePlansState = FetchableState<string[]>

interface PlansState {
    active: ActivePlansState,
    extraTrialDays: { productId: string, days: number }[],
    debug: string
}

const initialState: PlansState = {
    active: {
        data: undefined,
        currentRequestId: undefined,
        lastFetched: undefined
    },
    extraTrialDays: [],
    debug: "plans state",
}

export const plansSlice = createSlice({
    name: 'plans',
    initialState: initialState,
    reducers: {},
    extraReducers(builder) {
        builder.addCase(activePlansThunk.pending, (state, action) => {
            if (state.active.currentRequestId)
                return
            state.active.currentRequestId = action.meta.requestId
        })
        builder.addCase(activePlansThunk.fulfilled, (state, action) => {
            if (state.active.currentRequestId !== action.meta.requestId)
                return
            state.active.currentRequestId = undefined
            const minSubscriptionPrice = (sku: Sku) =>
                Math.min(...sku.offerings.filter(o => o.paymentType === "subscription").map(o => o.priceCents))
            state.active.data = action.payload.skus
                .slice()
                .sort((a: Sku, b: Sku) => minSubscriptionPrice(a) - minSubscriptionPrice(b))
                .map((sku: Sku) => sku.id)
            state.active.lastFetched = Date.now()
            state.extraTrialDays = action.payload.extraTrialDays || []
        })
        builder.addCase(activePlansThunk.rejected, (state, action) => {
            if (state.active.currentRequestId !== action.meta.requestId)
                return
            state.active.currentRequestId = undefined
        })
        builder.addCase(logOut, (state: PlansState) => {
            state = initialState;
        })
    },
})


// GET plans — the sku entities in the payload are absorbed by skusSlice

export const activePlansThunk = createAsyncThunk(
    'plans/fetchActivePlans',
    async ({ accessToken }: { accessToken: string }, { getState, requestId }) => {
        const state = getState() as RootState;
        const { currentRequestId } = state.plans.active;
        if (currentRequestId !== requestId)
            throw new Error("Request outdated");
        return await Api.getActivePlans(accessToken)
    }
)

const activePlansSelector = (state: RootState) => state.plans.active

export function useActivePlans(accessToken: string) {
    return useFetchableState<string[]>({
        selector: activePlansSelector,
        action: activePlansThunk({ accessToken })
    })
}

// Extra trial days granted to the current user for the products they already own.
// Shared across all plans, so exposed separately from the plan sku ids.
// Populated by activePlansThunk (empty when the user already has a subscription).
export function useExtraTrialDays(): { productId: string, days: number }[] {
    return useSelector((state: RootState) => state.plans.extraTrialDays)
}

export default plansSlice.reducer
